/* eslint-disable react/prop-types */
// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonAvatar from "components/ArgonAvatar";
import { useState, useEffect } from "react";
import { getAllTransactions } from "services/transactionService";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { AdminSeenViewButton, AdminUnreadBadge } from "components/AdminUnread";
import { ADMIN_UNREAD_MODULES } from "services/adminUnreadService";

// Images
import team2 from "assets/images/team-2.jpg";

const profileLinkSx = (enabled) => ({
  cursor: enabled ? "pointer" : "default",
  textDecoration: enabled ? "underline !important" : "none",
  textUnderlineOffset: enabled ? "2px" : undefined,
  textDecorationThickness: enabled ? "1px" : undefined,
  "&:hover": enabled
    ? {
        color: "#1976d2",
        textDecoration: "underline !important",
      }
    : {},
});

function Author({ image, name, email, studentsText, to }) {
  const hasProfilePath = Boolean(to);

  return (
    <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
      <ArgonBox mr={2}>
        <ArgonAvatar src={image} alt={name} name={name} size="sm" variant="rounded" />
      </ArgonBox>
      <ArgonBox display="flex" flexDirection="column">
        <ArgonTypography
          variant="button"
          fontWeight="medium"
          component={hasProfilePath ? Link : "span"}
          to={hasProfilePath ? to : undefined}
          color="text"
          sx={profileLinkSx(hasProfilePath)}
        >
          {name}
        </ArgonTypography>
        <ArgonTypography variant="caption" color="secondary">
          {email}
        </ArgonTypography>
        {studentsText ? (
          <ArgonTypography variant="caption" color="secondary">
            {studentsText}
          </ArgonTypography>
        ) : null}
      </ArgonBox>
    </ArgonBox>
  );
}

const getStudentNames = (transaction) => {
  if (Array.isArray(transaction.studentNames) && transaction.studentNames.length) {
    return transaction.studentNames.filter(Boolean);
  }

  if (Array.isArray(transaction.students) && transaction.students.length) {
    return transaction.students
      .map((student) => {
        if (typeof student === "string") return student;
        return student?.name || student?.fullName || "";
      })
      .filter(Boolean);
  }

  return [];
};

const getFeesTypeLabel = (feesType) => {
  if (!feesType) return "N/A";
  if (feesType === "CLASS_SESSION_FEES") return "TUTION FEES";
  if (feesType === "TUTOR_SALARY") return "TUTOR SALARY";
  return feesType.replace(/_/g, " ");
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  });

const AllTransactionTableData = (
  filters = {},
  handleViewClick = () => {},
  handleSeen = () => {}
) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 50,
  });
  const [totals, setTotals] = useState({
    creditAmount: 0,
    debitAmount: 0,
  });

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = {};

      // Add filters
      if (filters.fromDate) {
        params.fromdate = dayjs(filters.fromDate).format("YYYY-MM-DD");
      }
      if (filters.toDate) {
        params.todate = dayjs(filters.toDate).format("YYYY-MM-DD");
      }
      if (filters.method) {
        params.method = filters.method.toUpperCase();
      }
      if (filters.transactionType) {
        params.transactionType = filters.transactionType.toUpperCase();
      }
      if (filters.adminUnread !== undefined) {
        params.adminUnread = filters.adminUnread;
      }

      // Add pagination params
      if (filters.page) {
        params.page = filters.page;
      }
      if (filters.limit) {
        params.limit = filters.limit;
      }

      const response = await getAllTransactions(params);

      if (response.success) {
        setTransactions(response.data);
        setTotals({
          creditAmount: response.totals?.creditAmount || 0,
          debitAmount: response.totals?.debitAmount || 0,
        });
        // Store pagination metadata from backend
        if (response.pagination) {
          setPagination(response.pagination);
        }
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [filters]);

  const columns = [
    { name: "sno", align: "center" },
    // { name: "Timestamp", align: "left" },
    { name: "Payment Date", align: "left" },
    { name: "User", align: "left" },
    { name: "Transaction type", align: "left" },
    { name: "method", align: "left" },
    // { name: "payment type", align: "left" },
    { name: "Fees Type", align: "left" },
    {
      name: "credit amount",
      label: (
        <ArgonBox component="span" display="inline-flex" alignItems="center" gap={0.5}>
          <ArgonTypography
            component="span"
            variant="caption"
            fontWeight="bold"
            sx={{ color: "#7686a6ff", opacity: 0.7, fontSize: "inherit" }}
          >
            CREDIT AMOUNT
          </ArgonTypography>
          <ArgonTypography
            component="span"
            variant="caption"
            color="success"
            fontWeight="bold"
            sx={{ fontSize: "inherit" }}
          >
            ({formatCurrency(totals.creditAmount)})
          </ArgonTypography>
        </ArgonBox>
      ),
      align: "center",
      headerOpacity: 1,
    },
    {
      name: "debit amount",
      label: (
        <ArgonBox component="span" display="inline-flex" alignItems="center" gap={0.5}>
          <ArgonTypography
            component="span"
            variant="caption"
            fontWeight="bold"
            sx={{ color: "#7686a6ff", opacity: 0.7, fontSize: "inherit" }}
          >
            DEBIT AMOUNT
          </ArgonTypography>
          <ArgonTypography
            component="span"
            variant="caption"
            color="error"
            fontWeight="bold"
            sx={{ fontSize: "inherit" }}
          >
            ({formatCurrency(totals.debitAmount)})
          </ArgonTypography>
        </ArgonBox>
      ),
      align: "center",
      headerOpacity: 1,
    },
    { name: "Action", align: "center" },
  ];

  const rows = transactions.map((transaction, index) => {
    const isRegistrationFees = transaction.FeesType === "REGISTRATION_FEES";
    const user = isRegistrationFees
      ? transaction.tutorId || transaction.parentId
      : transaction.parentId || transaction.tutorId;
    const tutorId = transaction.tutorId?._id || transaction.tutorId?.id;
    const parentId = transaction.parentId?._id || transaction.parentId?.id;
    const userId = user?._id || user?.id;
    const isTutorUser = Boolean(userId && tutorId && userId === tutorId);
    const isParentUser = Boolean(userId && parentId && userId === parentId);
    const profilePath = isTutorUser
      ? `/tutors/profile/${userId}`
      : isParentUser
      ? `/parents/profile/${userId}`
      : undefined;

    const studentNames = getStudentNames(transaction);
    const userName =
      user?.fullName || user?.name
        ? `${user?.fullName || user?.name}${
            studentNames.length ? ` (${studentNames.join(", ")})` : ""
          }`
        : "N/A";
    const userEmail = user?.email || "N/A";
    const userImage = user?.profileImage || null;

    const studentsText = "";
    const displayPaymentDate =
      transaction.paymentDate ||
      transaction.tutorPaymentCycleId?.paymentDate ||
      transaction.paymentDateFallback ||
      transaction.financeId?.classSessionId?.paymentDate ||
      transaction.financeId?.classSessionId?.scheduleDate ||
      transaction.financeId?.classSessionId?.firstClassOn;
    const isUnread = Boolean(transaction.adminUnread);
    const unreadRowSx = isUnread
      ? {
          backgroundColor: "rgba(245, 54, 92, 0.035)",
          boxShadow: "inset 4px 0 0 #f5365c",
          "& td": {
            borderTop: "1px solid rgba(245, 54, 92, 0.24)",
            borderBottom: "1px solid rgba(245, 54, 92, 0.24) !important",
          },
          "& td:first-of-type": {
            borderLeft: "1px solid rgba(245, 54, 92, 0.24)",
          },
          "& td:last-of-type": {
            borderRight: "1px solid rgba(245, 54, 92, 0.24)",
          },
        }
      : {};

    return {
      rowSx: unreadRowSx,
      sno: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {index + 1}
        </ArgonTypography>
      ),
      Timestamp: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {dayjs(transaction.paymentDate || transaction.createdAt).format("DD MMMM YYYY")}
        </ArgonTypography>
      ),
      "Payment Date": (
        <ArgonBox display="flex" alignItems="center" gap={0.75}>
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {displayPaymentDate ? dayjs(displayPaymentDate).format("DD MMMM YYYY") : "-"}
          </ArgonTypography>
          <AdminUnreadBadge unread={isUnread} />
        </ArgonBox>
      ),
      User: (
        <Author
          image={userImage}
          name={userName}
          email={userEmail}
          studentsText={studentsText}
          to={profilePath}
        />
      ),
      "Transaction type": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {transaction.transactionType?.toUpperCase() || "N/A"}
        </ArgonTypography>
      ),
      method: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {transaction.method?.toUpperCase() || "N/A"}
        </ArgonTypography>
      ),
      // "payment type": (
      //   <ArgonTypography variant="caption" color="text" fontWeight="medium">
      //     {transaction.paymentType || "N/A"}
      //   </ArgonTypography>
      // ),
      "Fees Type": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {getFeesTypeLabel(transaction.FeesType)}
        </ArgonTypography>
      ),
      "credit amount": (
        <ArgonTypography
          variant="caption"
          color={transaction.transactionType === "CREDIT" ? "success" : "text"}
          fontWeight="medium"
        >
          {transaction.transactionType === "CREDIT"
            ? `₹${transaction.amount?.toLocaleString()}`
            : "-"}
        </ArgonTypography>
      ),
      "debit amount": (
        <ArgonTypography
          variant="caption"
          color={transaction.transactionType === "DEBIT" ? "error" : "text"}
          fontWeight="medium"
        >
          {transaction.transactionType === "DEBIT"
            ? `₹${transaction.amount?.toLocaleString()}`
            : "-"}
        </ArgonTypography>
      ),
      Action: (
        <AdminSeenViewButton
          module={ADMIN_UNREAD_MODULES.TRANSACTION}
          item={transaction}
          onView={handleViewClick}
          onSeen={handleSeen}
        />
      ),
    };
  });

  return {
    columns,
    rows,
    loading,
    fetchTransactions,
    pagination,
  };
};

export default AllTransactionTableData;
